/**
 * Travel — a body carried somewhere by an ability, over ticks.
 *
 * Every displacement in the roster used to land in ONE tick: NVDA's 3 m lunge,
 * Parallel Path's 9 m dash, Hover Slam's 8 m of altitude, Robotaxi's swap of
 * up to 20 m. Inside the sim that is fine; on a screen it is a teleport. The
 * remote body jumps a whole ability's length between two interpolated frames,
 * and the local body — predicted from a world that held no other bodies and
 * none of the server's effects — jumped it wrong and got snapped. The founder
 * measured it and called it: "instead of that many teleports we do some jump
 * or like dash or a roll".
 *
 * So a displacement is now a TRAVEL: an effect on the body that drives its
 * velocity for N ticks along a fixed direction on an ease-out, with the hit
 * resolving when the body ARRIVES (the lunge's cone) or as it PASSES (the
 * dash's cut). The profile is quadratic ease-out — fast off the mark, settling
 * to a stop — built from +, -, *, / only, so both runtimes step the same bits.
 *
 * Determinism holds the way it does everywhere else in this package: no clock,
 * no randomness, and the whole travel is readable off the effect (direction,
 * distance, duration, expiry), which is what lets the client restore one from
 * a snapshot mid-flight and finish it in step with the server.
 */
import { bodyRadius } from "./body.js";
import { MELEE_VERTICAL_REACH, TRAVEL_MAX_STEP } from "./constants.js";
import { removeEffect } from "./effects.js";
const TRAVEL_KINDS = ["lunge", "blink", "ride", "leap"];
export function isTravelKind(kind) {
    return kind === "lunge" || kind === "blink" || kind === "ride" || kind === "leap";
}
/** The travel this body is inside, or null. A body is inside at most one. */
export function findTravel(p) {
    for (const e of p.effects)
        if (isTravelKind(e.kind))
            return e;
    return null;
}
export function isTravelling(p) {
    return findTravel(p) !== null;
}
/**
 * Fraction of the distance covered after `k` of `n` ticks: quadratic ease-out,
 * `1 - (1 - k/n)^2`. The first tick covers about 2/n of the distance and the
 * last one about 1/n², which is what makes the body settle rather than stop.
 */
export function travelProgress(k, n) {
    if (k <= 0)
        return 0;
    if (k >= n)
        return 1;
    const r = 1 - k / n;
    return 1 - r * r;
}
/**
 * How many ticks a travel of `distance` takes: the ability's own duration, or
 * more if its first (largest) step would exceed `maxStep`. A 3 m roll keeps
 * its 3 ticks; a 20 m ride grows from 9 to 15 rather than clip.
 */
export function travelTicks(distance, minTicks, maxStep = TRAVEL_MAX_STEP) {
    const floor = Math.max(1, minTicks);
    if (distance <= 0)
        return floor;
    // First step of the ease-out is D * (1 - (1 - 1/n)^2) = D * (2n - 1) / n²,
    // which is under 2D/n; requiring 2D/n <= maxStep is the safe, simple bound.
    return Math.max(floor, Math.ceil((2 * distance) / maxStep));
}
/**
 * Starts a travel on `p`. One at a time: a new travel replaces any in flight
 * — except that a body being RIDDEN cannot leave the taxi by its own roll,
 * dash or leap. The ride was put on it by someone else and skips geometry;
 * cut short, it strands the body wherever the line had got to, inside a wall
 * or over the void. Returns null when refused; the caller drops its hit.
 *
 * `untilTick` is `tick + ticks + 1`: the effect is started by a cast resolving
 * AFTER this tick's movement, so its first step is next tick and its last is
 * `ticks` ticks from now; `expireEffects` drops it on the tick after that.
 */
export function startTravel(world, p, kind, dirX, dirY, dirZ, distance, ticks) {
    if (kind !== "ride" && findTravel(p)?.kind === "ride")
        return null;
    endTravel(p);
    const effect = {
        kind,
        untilTick: world.tick + ticks + 1,
        magnitude: distance,
        sourceId: p.id,
        ax: dirX,
        ay: dirY,
        az: dirZ,
        ticks,
    };
    p.effects.push(effect);
    return effect;
}
export function endTravel(p) {
    for (const kind of TRAVEL_KINDS)
        removeEffect(p, kind);
}
/** A ride and a leap drive the vertical too: no gravity, no jump, no walls (the ride is a taxi). */
export function isFlight(kind) {
    return kind === "leap" || kind === "ride";
}
/**
 * A body that passes THROUGH other bodies: the dash cuts what it crosses and
 * the ride is a taxi, exactly as the instant line and the swap they replace
 * went through everything. Body separation skips such a body for the length
 * of its travel; the lunge stops at contact instead and is separated as usual.
 */
export function ghostsThroughBodies(p) {
    for (const e of p.effects)
        if (e.kind === "blink" || e.kind === "ride")
            return true;
    return false;
}
/**
 * This tick's displacement for a travelling body, or null once it is spent.
 *
 * `k` is which tick of the curve this is, recovered from what is left of the
 * effect and its total, so a client that restored the effect from a snapshot
 * three ticks in continues from the fourth step, not the first.
 */
export function travelStep(world, p, effect, out) {
    const n = effect.ticks;
    const remaining = effect.untilTick - world.tick;
    if (n <= 0 || remaining <= 0 || !isTravelKind(effect.kind))
        return null;
    out.kind = effect.kind;
    const k = n - remaining + 1;
    let step = effect.magnitude * (travelProgress(k, n) - travelProgress(k - 1, n));
    let stopped = false;
    if (effect.kind === "lunge") {
        // The roll stops at the first enemy body in its way, at contact, so the
        // cone resolves with the target in front of it and separation has nothing
        // to undo. Checked every tick against LIVE bodies: a target that steps
        // into the roll is met where it now is, not where it was at the press.
        const clear = lungeClearance(world, p, effect.ax, effect.az, step);
        if (clear < step) {
            step = clear;
            stopped = true;
        }
    }
    out.dx = effect.ax * step;
    out.dy = effect.ay * step;
    out.dz = effect.az * step;
    out.stopped = stopped;
    return out;
}
/**
 * How far a lunge may advance along (dirX, dirZ) before touching an enemy
 * body: `max`, or the distance to contact (two body radii) with the nearest
 * body in the corridor. Nearest body wins; ties are impossible to observe (a
 * nearer body is nearer). A lunge that carried its caster the full distance
 * sailed straight PAST anyone standing closer and cut the empty air behind
 * them — the one target a point-blank lunge should never miss.
 */
export function lungeClearance(world, caster, dirX, dirZ, max) {
    const contact = bodyRadius(world) * 2;
    let travel = max;
    for (const target of world.players) {
        if (target.id === caster.id || target.phase !== "alive")
            continue;
        if (Math.abs(target.y - caster.y) > MELEE_VERTICAL_REACH)
            continue;
        const dx = target.x - caster.x;
        const dz = target.z - caster.z;
        const along = dx * dirX + dz * dirZ;
        if (along <= 0 || along - contact >= travel)
            continue;
        const lateral = Math.abs(dx * dirZ - dz * dirX);
        if (lateral > contact)
            continue;
        travel = Math.max(0, along - contact);
    }
    return travel;
}
//# sourceMappingURL=travel.js.map